<script setup lang="ts">
import { onMounted, ref, watch, watchEffect } from 'vue';
import { SearchFilter, FilterOption } from '../../../search-panel-types';
import FilterFieldLayout from './FilterFieldLayout.vue';
import useLocale from '../../../composables/useLocale';

const emit = defineEmits<{
  (e: 'filters-updated', filter: SearchFilter): void;
}>();

type Props = {
  items: FilterOption[];
  fieldKey: string;
  label: string;
  selected: { value: SearchFilter };
  customRange: number[] | null;
  customLabel?: boolean;
  multiple?: boolean;
};
const props = defineProps<Props>();

const { t } = useLocale();

// Do not allow negative values
const findMinRangeValue = () => {
  const min = props.items.reduce(
    (prev: number, cur: FilterOption) => (Number(prev) < Number(cur.value) ? prev : Number(cur.value)),
    Infinity,
  );

  if (min === Infinity) {
    return 0;
  }

  return Math.max(min, 0);
};

const findMaxRangeValue = () =>
  props.items.reduce(
    (prev: number | null, cur: FilterOption) => (Number(prev) > Number(cur.value) ? prev : Number(cur.value)),
    null,
  ) ?? 0;

const minRangeValue = ref(findMinRangeValue());
const maxRangeValue = ref(findMaxRangeValue());
const defaultRange = [minRangeValue.value, maxRangeValue.value];
const range = ref<number[]>([]);
const foundedMinRangeValue = ref<number>(minRangeValue.value);
const foundedMaxRangeValue = ref<number>(maxRangeValue.value);

range.value = defaultRange;
watch(
  () => props.customRange,
  () => {
    if (props.customRange !== null) {
      range.value = props.customRange;
      return;
    }
    range.value = defaultRange;
  },
);

watchEffect(() => {
  if (props.customRange && props.customRange.length === 0) {
    foundedMinRangeValue.value = findMinRangeValue();
    foundedMaxRangeValue.value = findMaxRangeValue();
    if (Number(foundedMaxRangeValue.value) > maxRangeValue.value) {
      maxRangeValue.value = Number(props.customRange[1]);
    }
    if (Number(foundedMinRangeValue.value) < minRangeValue.value) {
      minRangeValue.value = Number(props.customRange[0]);
    }
    if (!props.selected.value[props.fieldKey]) {
      range.value = [minRangeValue.value, maxRangeValue.value];
    }
  } else {
    foundedMinRangeValue.value = props.customRange !== null ? props.customRange[0] : minRangeValue.value;
    foundedMaxRangeValue.value = props.customRange !== null ? props.customRange[1] : maxRangeValue.value;
    // if we get new values from api we need to setup right range but only for cases when the range is less
    // than minimum or more than maximum
    if (Number(foundedMaxRangeValue.value) > maxRangeValue.value && props.customRange) {
      maxRangeValue.value = Number(props.customRange[1]);
    }
    if (Number(foundedMinRangeValue.value) < minRangeValue.value && props.customRange) {
      minRangeValue.value = Number(props.customRange[0]);
    }
    if (!props.selected.value[props.fieldKey]) {
      if (props.customRange) {
        range.value = props.customRange;
      } else {
        range.value = [minRangeValue.value, maxRangeValue.value];
      }
    }
  }
});

const handleUpdate = (rangeValue: number[]) => {
  emit('filters-updated', {
    [props.fieldKey]: [
      { value: String(rangeValue[0]), label: '', count: 1 },
      { value: String(rangeValue[1]), label: '', count: 1 },
    ],
  });
};

const rangeContainerRef = ref<HTMLElement | null>(null);

/**
 * Disable tab focus for the range slider. Hide vertical and horizontal splitters
 * in the number input field from screen readers.
 */
onMounted(() => {
  const slider = rangeContainerRef.value?.querySelector('.v-slider__container');
  if (slider) {
    slider.querySelectorAll('.v-slider-thumb ').forEach((thumb) => {
      thumb.setAttribute('tabindex', '-1');
      thumb.setAttribute('aria-hidden', 'true');
    });
  }

  const splitters = rangeContainerRef.value?.querySelectorAll('hr');
  if (splitters) {
    splitters.forEach((splitter) => {
      splitter.setAttribute('aria-hidden', 'true');
    });
  }
});

const onMinChanged = (val: number) => {
  let value = val;
  if (props.fieldKey === 'duration') {
    value = val * 60;
  }
  if (Number.isFinite(value)) {
    range.value[0] = value;
    handleUpdate(range.value);
  }
};

const onMaxChanged = (val: number) => {
  let value = val;
  if (props.fieldKey === 'duration') {
    value = val * 60;
  }
  if (Number.isFinite(value)) {
    range.value[1] = Math.min(value, Math.min(Number.MAX_SAFE_INTEGER, maxRangeValue.value));
    handleUpdate(range.value);
  }
};
</script>

<template>
  <template v-if="!props.multiple">
    <FilterFieldLayout v-show="props.items?.length > 0" :label="props.label ?? ''">
      <template #content>
        <div class="range-container">
          <v-range-slider
            v-model="range"
            color="primary"
            hide-details
            thumb-label="always"
            :max="maxRangeValue"
            :min="minRangeValue"
            step="1"
            track-size="1"
            thumb-size="12"
            @end="handleUpdate"
          >
            <template #thumb-label="rangeProps">
              <slot v-if="props.customLabel" :value="rangeProps.modelValue"></slot>
              <span v-else>
                {{ rangeProps.modelValue }}
              </span>
            </template>
          </v-range-slider>
        </div>
      </template>
    </FilterFieldLayout>
  </template>
  <template v-else>
    <div class="range-container d-flex flex-column ga-2" ref="rangeContainerRef">
      <v-range-slider
        v-model="range"
        color="primary"
        hide-details
        thumb-label="always"
        :max="maxRangeValue"
        :min="minRangeValue"
        step="1"
        track-size="1"
        thumb-size="12"
        @end="handleUpdate"
        :aria-hidden="true"
      >
        <template #thumb-label="rangeProps">
          <slot v-if="props.customLabel" :value="rangeProps.modelValue"></slot>
          <span v-else>
            {{ rangeProps.modelValue }}
          </span>
        </template>
      </v-range-slider>
      <div class="d-flex justify-space-between ga-2">
        <v-number-input
          :model-value="props.fieldKey === 'duration' ? Number(range[0] / 60) : Number(range[0])"
          density="compact"
          :max="maxRangeValue"
          :min="minRangeValue"
          controlVariant="split"
          :aria-label="t('content_search.label_number_input_min')"
          @update:modelValue="onMinChanged"
          variant="outlined"
          bg-color="surface-bright"
          color="primary"
          class="number-inputs"
          :hide-details="true"
        ></v-number-input>
        <v-col class="ma-2 pa-0" cols="auto" :aria-hidden="true">
          <span>&mdash;</span>
        </v-col>
        <v-number-input
          :model-value="props.fieldKey === 'duration' ? Number(range[1] / 60) : Number(range[1])"
          density="compact"
          :max="maxRangeValue"
          :min="minRangeValue"
          control-variant="split"
          :aria-label="t('content_search.label_number_input_max')"
          @update:modelValue="onMaxChanged"
          variant="outlined"
          bg-color="surface-bright"
          color="primary"
          class="number-inputs"
          :hide-details="true"
        ></v-number-input>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
@import 'vuetify/settings';

.range-container {
  padding: 35px 10px 15px 10px;
}
:deep(.v-slider-thumb__label) {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
  &:before {
    color: rgb(var(--v-theme-primary));
  }
}

.number-inputs {
  max-height: 40px;
  :deep(.v-field__input) {
    text-align: center !important;
    font-size: 0.875rem !important;
  }

  :deep(.v-btn) {
    color: rgb(var(--v-theme-on-surface)) !important;
    background-color: transparent !important;
  }

  :deep(.v-btn[disabled]) {
    opacity: 0.26 !important; /* Apply opacity when button is disabled */
  }

  @media #{map-get($display-breakpoints, 'sm-and-up')} {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height)) !important;
    }
  }

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height) + 12px) !important;
    }
  }

  @media (max-width: 400px) {
    :deep(.v-btn--icon) {
      width: calc(var(--v-btn-height) - 12px) !important;
    }
  }
}
</style>
